import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  useOnboarding, useSaveOnboarding, useSubmitOnboarding,
  useUploadOnboardingFile, useDeleteOnboardingFile,
  type OnboardingData, type PropertyEntry,
} from '../../hooks/useOnboarding';
import {
  Building2, Home, SlidersHorizontal, Palette,
  ImagePlus, Loader2, Check, CloudUpload, FileText, X, Send, AlertCircle, Globe, ArrowRight, Sparkles,
} from 'lucide-react';
import { AutoTextarea } from '../../components/AutoTextarea';
import { trackConversion } from '../../lib/analytics';

// Same pricing formula as AgreementPage — used for the browser-side Purchase value.
const PURCHASE_BASE = 49, PURCHASE_EXTRA = 15, PURCHASE_YEARLY_MONTHS = 11;
const purchaseValue = (units: number, billing: 'monthly' | 'yearly') => {
  const m = PURCHASE_BASE + PURCHASE_EXTRA * (Math.max(1, units) - 1);
  return billing === 'yearly' ? m * PURCHASE_YEARLY_MONTHS : m;
};

const emptyProperty = (): PropertyEntry => ({
  name: '', address: '', bedrooms: '', bathrooms: '', maxGuests: '', description: '', amenities: '',
});

const defaultData = (): OnboardingData => ({
  properties: [emptyProperty()],
  booking: { minNights: '', checkInTime: '', checkOutTime: '', houseRules: '', cancellationPolicy: '', seasonalPricing: '' },
  guestGuide: { wifiName: '', wifiPassword: '', accessInstructions: '', localRecommendations: '' },
  channels: { icalBooking: '', icalAirbnb: '', otherIcal: '' },
  branding: { preferredDomain: '', brandColors: '', languages: [], notes: '' },
  defaults: { checkInTime: '15:00', checkOutTime: '11:00', houseRules: '', cancellationPolicy: '' },
  extraNotes: '',
});

interface FormState {
  legalName: string; taxId: string; billingAddress: string;
  contactPhone: string; contactWhatsapp: string;
  propertyCount: string; propertyType: string;
  currentWebsite: string; listingBooking: string; listingAirbnb: string;
  listingVrbo: string; listingOther: string;
  data: OnboardingData;
}

const LANGUAGES =['ES', 'EN', 'DE', 'FR', 'IT', 'NL', 'PT', 'PL', 'NO', 'SV', 'DA', 'FI', 'RU', 'UK', 'ZH', 'JA', 'AR'];

// Curated palettes for vacation rentals — each a small set of hex colours with a name.
// Selecting one fills the existing comma-separated `branding.brandColors` value.
const BRAND_PALETTES: { id: string; nameKey: string; nameFallback: string; colors: string[] }[] = [
  { id: 'costa', nameKey: 'portal.onboarding.palette.costa', nameFallback: 'Costa', colors: ['#0E7C86', '#E9DBC7', '#1F3A47'] },
  { id: 'mediterraneo', nameKey: 'portal.onboarding.palette.mediterraneo', nameFallback: 'Mediterráneo', colors: ['#1D4E89', '#FFFFFF', '#7FB7D9'] },
  { id: 'terracota', nameKey: 'portal.onboarding.palette.terracota', nameFallback: 'Terracota', colors: ['#C4623A', '#F3E9DC', '#7A4A38'] },
  { id: 'bosque', nameKey: 'portal.onboarding.palette.bosque', nameFallback: 'Bosque', colors: ['#2F5D3A', '#D8D2C4', '#8A8170'] },
  { id: 'elegante', nameKey: 'portal.onboarding.palette.elegante', nameFallback: 'Elegante', colors: ['#1B2A4A', '#C9A24B', '#F5F2EB'] },
];

// Initials from a name: first letters of up to 2 words, uppercase. Falls back to "K".
const monogramInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'K';
  return words.slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
};

export default function OnboardingPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { data: remote, isLoading } = useOnboarding();
  const save = useSaveOnboarding();
  const submit = useSubmitOnboarding();
  const upload = useUploadOnboardingFile();
  const del = useDeleteOnboardingFile();

  const [form, setForm] = useState<FormState | null>(null);
  const [savedAt, setSavedAt] = useState<string>('');
  const [pendingColor, setPendingColor] = useState('#047857');
  const [pendingLang, setPendingLang] = useState('');
  const [noColors, setNoColors] = useState(false);
  const [noLogo, setNoLogo] = useState(false);
  const hydrated = useRef(false);

  // Hydrate local form once from the server.
  useEffect(() => {
    if (remote && !hydrated.current) {
      hydrated.current = true;
      const p = remote.profile;
      const b = (p.data as OnboardingData | undefined)?.branding;
      setNoColors(Boolean(b?.noBrandColors));
      setNoLogo(Boolean(b?.needsLogoDesign));
      setForm({
        legalName: p.legalName ?? '',
        taxId: p.taxId ?? '',
        billingAddress: p.billingAddress ?? '',
        contactPhone: p.contactPhone ?? '',
        contactWhatsapp: p.contactWhatsapp ?? '',
        propertyCount: p.propertyCount != null ? String(p.propertyCount) : '',
        propertyType: p.propertyType ?? '',
        currentWebsite: p.currentWebsite ?? '',
        listingBooking: p.listingBooking ?? '',
        listingAirbnb: p.listingAirbnb ?? '',
        listingVrbo: p.listingVrbo ?? '',
        listingOther: p.listingOther ?? '',
        data: { ...defaultData(), ...(p.data as OnboardingData) },
      });
    }
  }, [remote]);

  const persist = useCallback(async (state: FormState) => {
    await save.mutateAsync({
      legalName: state.legalName || null,
      taxId: state.taxId || null,
      billingAddress: state.billingAddress || null,
      contactPhone: state.contactPhone || null,
      contactWhatsapp: state.contactWhatsapp || null,
      propertyCount: state.propertyCount ? Number(state.propertyCount) : null,
      propertyType: state.propertyType || null,
      currentWebsite: state.currentWebsite || null,
      listingBooking: state.listingBooking || null,
      listingAirbnb: state.listingAirbnb || null,
      listingVrbo: state.listingVrbo || null,
      listingOther: state.listingOther || null,
      data: state.data,
    });
    setSavedAt(new Date().toLocaleTimeString());
  }, [save]);

  // Debounced autosave whenever the form changes.
  useEffect(() => {
    if (!form || !hydrated.current) return;
    const id = setTimeout(() => { void persist(form); }, 1500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const signed = remote?.agreement.signed ?? false;
  const paid = remote?.payment.status === 'paid';
  const submitted = remote?.profile.status === 'submitted';
  const doneForYou = remote?.profile.setupMode === 'done_for_you';

  // Meta Purchase (browser side). Fires only on the post-checkout redirect (?paid=1).
  // Uses eventID `purchase_<stripeSessionId>` from the `sid` query param so it dedups
  // against the authoritative server-side Purchase sent from the Stripe webhook.
  useEffect(() => {
    if (!paid || !remote) return;
    const qp = new URLSearchParams(window.location.search);
    if (qp.get('paid') !== '1') return; // only right after checkout, not on revisits
    try {
      if (sessionStorage.getItem('kx_evt_purchase')) return;
      sessionStorage.setItem('kx_evt_purchase', '1');
    } catch { /* ignore */ }
    const sid = qp.get('sid') || '';
    const units = remote.payment.units ?? 1;
    const billing = remote.payment.billing ?? 'monthly';
    trackConversion(
      'app_purchase_complete',
      { units, billing, value: purchaseValue(units, billing), currency: 'EUR' },
      'Purchase',
      sid ? { eventID: `purchase_${sid}` } : undefined,
    );
  }, [paid, remote]);

  if (isLoading || !form) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>;
  }

  // ── update helpers ──
  const set = (patch: Partial<FormState>) => setForm(f => f ? { ...f, ...patch } : f);
  const setData = (patch: Partial<OnboardingData>) => setForm(f => f ? { ...f, data: { ...f.data, ...patch } } : f);
  const setSection = <K extends keyof OnboardingData>(key: K, patch: Partial<OnboardingData[K]>) =>
    setForm(f => f ? { ...f, data: { ...f.data, [key]: { ...(f.data[key] as object), ...patch } } } : f);

  const toggleInArray = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('portal.onboarding.title')}</h1>
        <p className="text-slate-500 mt-1">{t('portal.onboarding.subtitle')}</p>
      </div>

      {!signed && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertCircle size={20} className="shrink-0" />
          <span>{t('portal.onboarding.signFirst')}{' '}
            <Link to="/portal/agreement" className="font-semibold underline">{t('portal.onboarding.goSign')}</Link>
          </span>
        </div>
      )}

      {signed && !paid && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertCircle size={20} className="shrink-0" />
          <span>{t('portal.onboarding.payFirst')}{' '}
            <Link to="/portal/payment" className="font-semibold underline">{t('portal.onboarding.goPay')}</Link>
          </span>
        </div>
      )}

      {submitted && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          <Check size={20} className="shrink-0" />
          <span>{t('portal.onboarding.submittedBanner')}</span>
        </div>
      )}

      {/* 1. Identity & billing */}
      <Section icon={Building2} title={t('portal.onboarding.sec.identity')}>
        <Grid>
          <Input label={t('portal.onboarding.f.legalName')} value={form.legalName} onChange={v => set({ legalName: v })} />
          <Input label={t('portal.onboarding.f.taxId')} value={form.taxId} onChange={v => set({ taxId: v })} />
        </Grid>
        <Input label={t('portal.onboarding.f.billingAddress')} value={form.billingAddress} onChange={v => set({ billingAddress: v })} />
        <Grid>
          <Input label={t('portal.onboarding.f.phone')} value={form.contactPhone} onChange={v => set({ contactPhone: v })} />
          <Input label={t('portal.onboarding.f.whatsapp')} value={form.contactWhatsapp} onChange={v => set({ contactWhatsapp: v })} />
        </Grid>
      </Section>

      {/* 2. Properties — managed in ONE place (/portal/manage/properties) */}
      <Section icon={Home} title={t('portal.onboarding.sec.properties')}>
        <Grid>
          <Select label={t('portal.onboarding.f.propertyType')} value={form.propertyType} onChange={v => set({ propertyType: v })}
            options={[
              ['', '—'], ['apartment', t('portal.onboarding.ptype.apartment')], ['villa', t('portal.onboarding.ptype.villa')],
              ['house', t('portal.onboarding.ptype.house')], ['complex', t('portal.onboarding.ptype.complex')], ['mixed', t('portal.onboarding.ptype.mixed')],
            ]} />
          <Input label={t('portal.onboarding.f.propertyCount')} type="number" value={form.propertyCount} onChange={v => set({ propertyCount: v })} />
        </Grid>

        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mt-2 space-y-3">
          <p className="text-sm text-slate-600">
            {t('portal.onboarding.propertiesManageHint', 'Añade y edita tus alojamientos (datos, fotos y precios) en su propio espacio. Todo se guarda en un solo lugar.')}
          </p>
          <Link
            to="/portal/manage/properties"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors"
          >
            {t('portal.onboarding.addPropertiesLink', 'Añade tus alojamientos')} <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* 2b. Existing online presence */}
      <Section icon={Globe} title={t('portal.onboarding.sec.presence', '¿Dónde apareces ahora?')}>
        {doneForYou && (
          <div className="flex items-start gap-2 rounded-lg bg-teal-50 border border-teal-100 p-3 text-sm text-teal-800 -mt-1">
            <Sparkles size={16} className="shrink-0 mt-0.5 text-teal-600" />
            <span>{t('portal.onboarding.presenceDfyHint', 'Pega aquí los enlaces de tus anuncios — los copiaremos por ti.')}</span>
          </div>
        )}
        <p className="text-sm text-slate-500 -mt-1">{t('portal.onboarding.presenceHint', 'Cuéntanos dónde se puede reservar o ver tu alojamiento hoy. Todo es opcional.')}</p>
        <Input label={t('portal.onboarding.f.currentWebsite', 'Tu web actual')} value={form.currentWebsite} onChange={v => set({ currentWebsite: v })} placeholder="https://mialojamiento.com" />
        <Grid>
          <Input label={t('portal.onboarding.f.listingBooking', 'Anuncio en Booking.com')} value={form.listingBooking} onChange={v => set({ listingBooking: v })} placeholder="https://booking.com/…" />
          <Input label={t('portal.onboarding.f.listingAirbnb', 'Anuncio en Airbnb')} value={form.listingAirbnb} onChange={v => set({ listingAirbnb: v })} placeholder="https://airbnb.com/…" />
        </Grid>
        <Grid>
          <Input label={t('portal.onboarding.f.listingVrbo', 'Anuncio en Vrbo')} value={form.listingVrbo} onChange={v => set({ listingVrbo: v })} placeholder="https://vrbo.com/…" />
          <Input label={t('portal.onboarding.f.listingOther', 'Otro')} value={form.listingOther} onChange={v => set({ listingOther: v })} placeholder={t('portal.onboarding.f.listingOtherPlaceholder', 'Otro portal o enlace')} />
        </Grid>
      </Section>

      {/* 3. Defaults for all properties */}
      <Section icon={SlidersHorizontal} title={t('portal.onboarding.sec.defaults', 'Valores por defecto para todos tus alojamientos')}>
        <p className="text-sm text-slate-500 -mt-1">
          {t('portal.onboarding.defaultsHint', 'Opcional. Estos valores se rellenarán automáticamente al crear cada nuevo alojamiento. Podrás cambiarlos en cada uno.')}
        </p>
        <Grid>
          <Input label={t('portal.onboarding.f.checkIn')} value={form.data.defaults.checkInTime} onChange={v => setSection('defaults', { checkInTime: v })} placeholder="15:00" />
          <Input label={t('portal.onboarding.f.checkOut')} value={form.data.defaults.checkOutTime} onChange={v => setSection('defaults', { checkOutTime: v })} placeholder="11:00" />
        </Grid>
        <Textarea label={t('portal.onboarding.f.houseRules')} value={form.data.defaults.houseRules} onChange={v => setSection('defaults', { houseRules: v })} />
        <Textarea label={t('portal.onboarding.f.cancellation')} value={form.data.defaults.cancellationPolicy} onChange={v => setSection('defaults', { cancellationPolicy: v })} />
      </Section>

      {/* 7. Branding & languages */}
      <Section icon={Palette} title={t('portal.onboarding.sec.branding')}>
        <Input label={t('portal.onboarding.f.preferredDomain')} value={form.data.branding.preferredDomain} onChange={v => setSection('branding', { preferredDomain: v })} placeholder="mialojamiento.com" />

        {/* Brand colours — pick & add multiple, or pick a curated palette */}
        {(() => {
          const colors = form.data.branding.brandColors.split(',').map(c => c.trim()).filter(Boolean);
          const setColors = (arr: string[]) => setSection('branding', { brandColors: arr.join(',') });
          const selectedPalette = form.data.branding.selectedPalette ?? '';
          const applyPalette = (p: typeof BRAND_PALETTES[number]) =>
            setSection('branding', { brandColors: p.colors.join(','), selectedPalette: p.id, noBrandColors: true });
          const toggleNoColors = (on: boolean) => {
            setNoColors(on);
            setSection('branding', on ? { noBrandColors: true } : { noBrandColors: false, selectedPalette: '' });
          };
          return (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.onboarding.f.brandColors', 'Colores de marca')}</label>
              <div className="flex flex-wrap items-center gap-2">
                {colors.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white pl-1.5 pr-2 py-1 text-xs">
                    <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: c }} />
                    <span className="font-mono text-slate-600">{c}</span>
                    <button type="button" aria-label="Quitar" onClick={() => setColors(colors.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500">×</button>
                  </span>
                ))}
                <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(pendingColor) ? pendingColor : '#047857'} onChange={e => setPendingColor(e.target.value)} className="w-9 h-9 rounded border border-slate-300 cursor-pointer p-0.5" aria-label={t('portal.onboarding.f.pickColor', 'Elegir color')} />
                <input value={pendingColor} onChange={e => setPendingColor(e.target.value)} placeholder="#047857"
                  className="w-28 px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500" aria-label={t('portal.onboarding.f.hexColor', 'Color en hex')} />
                <button type="button" onClick={() => { const c = pendingColor.trim(); if (c && !colors.includes(c)) setColors([...colors, c]); }}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium">+ {t('portal.onboarding.f.addColor', 'Añadir color')}</button>
              </div>

              {/* "I don't have brand colours yet" — reveal curated palettes */}
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm text-slate-600 select-none">
                <input type="checkbox" checked={noColors} onChange={e => toggleNoColors(e.target.checked)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                {t('portal.onboarding.f.noBrandColors', 'No tengo colores de marca todavía')}
              </label>

              {noColors && (
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-2">{t('portal.onboarding.paletteHint', 'Elige una paleta para empezar. Podrás ajustarla con el selector de arriba.')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {BRAND_PALETTES.map(p => {
                      const active = selectedPalette === p.id;
                      return (
                        <button key={p.id} type="button" onClick={() => applyPalette(p)}
                          className={`text-left rounded-lg border p-2.5 transition-colors ${active ? 'border-teal-500 ring-2 ring-teal-200 bg-teal-50/40' : 'border-slate-200 hover:border-teal-300'}`}>
                          <div className="flex gap-1 mb-2">
                            {p.colors.map((c, i) => <span key={i} className="flex-1 h-7 rounded" style={{ background: c }} />)}
                          </div>
                          <span className="flex items-center gap-1 text-xs font-medium text-slate-700">
                            {active && <Check size={12} className="text-teal-600" />}{t(p.nameKey, p.nameFallback)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Logo — upload yours, or let the Kanarix team design one */}
        {(() => {
          const firstColor = form.data.branding.brandColors.split(',').map(c => c.trim()).filter(Boolean)[0] || '#0E7C86';
          const initials = monogramInitials(form.legalName);
          const toggleNoLogo = (on: boolean) => {
            setNoLogo(on);
            setSection('branding', { needsLogoDesign: on });
          };
          return (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.onboarding.f.logo', 'Logo')}</label>
              {!noLogo && (
                <UploadButton category="logo" label={t('portal.onboarding.uploadLogo')} uploading={upload.isPending} onPick={f => upload.mutate({ file: f, category: 'logo' })} />
              )}
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm text-slate-600 select-none">
                <input type="checkbox" checked={noLogo} onChange={e => toggleNoLogo(e.target.checked)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                {t('portal.onboarding.f.noLogo', 'No tengo logo')}
              </label>

              {noLogo && (
                <div className="mt-3 flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                  <span aria-hidden className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-white text-xl font-bold shrink-0 shadow-sm" style={{ background: firstColor }}>
                    {initials}
                  </span>
                  <div className="text-sm">
                    <p className="font-medium text-slate-700">{t('portal.onboarding.monogramTitle', 'Propuesta provisional')}</p>
                    <p className="text-slate-500 mt-0.5">{t('portal.onboarding.needsLogoNote', 'Nuestro equipo diseñará un logo para ti.')}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Languages — predefined + custom */}
        <CheckGroup label={t('portal.onboarding.f.languages')} options={LANGUAGES}
          selected={form.data.branding.languages.filter(l => LANGUAGES.includes(l))} onToggle={v => setSection('branding', { languages: toggleInArray(form.data.branding.languages, v) })} />
        <div className="flex flex-wrap items-center gap-2">
          {form.data.branding.languages.filter(l => !LANGUAGES.includes(l)).map((l, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs text-teal-800">
              {l}<button type="button" aria-label="Quitar" onClick={() => setSection('branding', { languages: form.data!.branding.languages.filter(x => x !== l) })} className="text-teal-400 hover:text-rose-500">×</button>
            </span>
          ))}
          <input value={pendingLang} onChange={e => setPendingLang(e.target.value)} placeholder={t('portal.onboarding.f.otherLanguage', 'Otro idioma')}
            className="w-32 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
          <button type="button" onClick={() => { const v = pendingLang.trim().toUpperCase(); if (v && !form.data!.branding.languages.includes(v)) { setSection('branding', { languages: [...form.data!.branding.languages, v] }); setPendingLang(''); } }}
            className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium">+ {t('portal.onboarding.f.addLanguage', 'Añadir idioma')}</button>
        </div>
        <Textarea label={t('portal.onboarding.f.brandingNotes')} value={form.data.branding.notes} onChange={v => setSection('branding', { notes: v })} />
      </Section>

      {/* 8. Files */}
      <Section icon={ImagePlus} title={t('portal.onboarding.sec.files')}>
        <p className="text-sm text-slate-500 -mt-1">{t('portal.onboarding.filesHint')}</p>
        <div className="flex flex-wrap gap-2">
          <UploadButton category="photo" label={t('portal.onboarding.uploadPhotos')} uploading={upload.isPending} onPick={f => upload.mutate({ file: f, category: 'photo' })} />
          <UploadButton category="document" label={t('portal.onboarding.uploadDocs')} uploading={upload.isPending} onPick={f => upload.mutate({ file: f, category: 'document' })} />
        </div>
        {upload.isError && <p className="text-sm text-rose-600">{t('portal.onboarding.uploadError')}</p>}
        <FileList files={remote?.files ?? []} onDelete={async id => { if ((await confirm({ title: t('portal.onboarding.deleteFileTitle', 'Eliminar archivo'), message: t('portal.onboarding.deleteFileConfirm', '¿Eliminar este archivo?'), confirmLabel: t('common.deleteYes', 'Sí, eliminar'), tone: 'danger' })) === 'confirm') del.mutate(id); }} />
      </Section>

      {/* 9. Extra notes */}
      <Section icon={FileText} title={t('portal.onboarding.sec.extra')}>
        <Textarea label={t('portal.onboarding.f.extraNotes')} value={form.data.extraNotes} onChange={v => setData({ extraNotes: v })} />
      </Section>

      {/* Sticky save/submit bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white/90 backdrop-blur border-t border-slate-200 px-4 sm:px-6 py-3 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            {save.isPending ? <><Loader2 size={14} className="animate-spin" /> {t('portal.onboarding.saving')}</>
              : savedAt ? <><Check size={14} className="text-emerald-500" /> {t('portal.onboarding.savedAt', { time: savedAt })}</>
              : t('portal.onboarding.autosave')}
          </span>
          <button
            onClick={() => { trackConversion('app_onboarding_submit', {}, 'SubmitApplication'); submit.mutate(); }}
            disabled={!signed || !paid || submit.isPending || submitted}
            className="flex items-center gap-2 py-2.5 px-5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {submit.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitted ? t('portal.onboarding.submitted') : t('portal.onboarding.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Building blocks ───────────────────────────────────────────────────────────

function Section({ icon: Icon, title, children }: { icon: typeof Home; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-3">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
        <Icon size={18} className="text-teal-600" /> {title}
      </h2>
      {children}
    </section>
  );
}

const Grid = ({ children }: { children: React.ReactNode }) =>
  <div className="grid sm:grid-cols-2 gap-3">{children}</div>;

const fieldCls = 'w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';

function Input({ label, value, onChange, type = 'text', placeholder, hint }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; hint?: string;
}) {
  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={fieldCls} />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, hint }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string;
}) {
  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
      <AutoTextarea value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} rows={3} className={fieldCls} />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className={fieldCls}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function CheckGroup({ label, options, selected, onToggle }: {
  label: string; options: string[]; selected: string[]; onToggle: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-600 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const on = selected.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => onToggle(opt)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                on ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'
              }`}>
              {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UploadButton({ label, category, uploading, onPick }: {
  label: string; category: string; uploading: boolean; onPick: (f: File) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const accept = category === 'document' ? 'application/pdf,image/*' : 'image/*';
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
        className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:border-teal-400 hover:text-teal-600 transition-colors disabled:opacity-50">
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />} {label}
      </button>
      <input ref={ref} type="file" accept={accept} className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }} />
    </>
  );
}

function FileList({ files, onDelete }: { files: { id: number; category: string; filename: string }[]; onDelete: (id: number) => void }) {
  const { t } = useTranslation();
  const labels = useMemo(() => ({ photo: '🖼️', document: '📄', logo: '🎨', other: '📎' } as Record<string, string>), []);
  if (files.length === 0) return null;
  return (
    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg mt-2">
      {files.map(f => (
        <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span>{labels[f.category] ?? '📎'}</span>
          <span className="flex-1 truncate text-slate-700">{f.filename}</span>
          <button onClick={() => onDelete(f.id)} className="text-slate-400 hover:text-rose-500" title={t('common.delete')}>
            <X size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}
